import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import SteamIcon from '@/components/site/SteamIcon'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Drop-down notice asking a member to link their Steam account.
 *
 * Staff can no longer type SteamID64s in for people, and the reserved slot on
 * the game server is granted on that id — so a member who never links is a
 * member the server cannot recognise. This is the nag that closes that gap.
 *
 * Renders nothing at all once the account is verified, so it disappears the
 * moment it stops being true rather than sitting there as permanent furniture.
 * Dismissal lasts for the browser session only: the ask is not optional, it is
 * just not worth repeating twice in the same sitting.
 */

const DISMISS_KEY = 'tll_steam_banner_dismissed'

export default function SteamLinkBanner() {
  const { T } = useLanguage()
  const [needed, setNeeded] = useState(false)
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      return false // private mode throws on access; nagging beats crashing
    }
  })

  useEffect(() => {
    let alive = true
    fetch('/api/steam/me', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setNeeded(!d.verified) })
      .catch(() => { /* offline or logged out: say nothing */ })
    return () => { alive = false }
  }, [])

  if (!needed || dismissed) return null

  function dismiss() {
    setDismissed(true)
    try { sessionStorage.setItem(DISMISS_KEY, '1') } catch { /* ignore */ }
  }

  return (
    <div className="animate-in slide-in-from-top-4 fade-in duration-500 border-b border-amber-500/25 bg-amber-500/10">
      {/* Tek satır: başlık ve gerekçe yan yana, alt alta değil. Gerekçe
          şeritte kalır — ikna cümlesinin yeri burasıdır, "henüz bağlamadın"
          diyen ekran budur. Dar ekranda gerekçe düşer: orada satırı ikiye
          katlamaktansa çağrının görünür kalması önemli. */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-4 py-2 md:px-6">
        <SteamIcon size={14} />
        <p className="text-[13px] font-medium text-amber-100">{T.members.steamLinkTitle}</p>
        <p className="hidden min-w-0 flex-1 truncate text-xs text-amber-200/70 lg:block">
          {T.members.steamLinkBody}
        </p>
        <a href="/api/steam/link" className="ml-auto">
          <Button size="sm" className="h-7 gap-1.5 px-2 text-[11px]">
            <SteamIcon size={12} />
            {T.members.steamLinkCta}
          </Button>
        </a>
        <button
          onClick={dismiss}
          aria-label={T.members.cancel}
          className="rounded-sm p-1 text-amber-200/60 transition-colors hover:bg-white/10 hover:text-amber-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
